import type {
  TopologyV2ResolveResponse,
  TopologyV2ViewCatalogItemschema,
  TopologyV2ViewSaveResponse,
  TopologyV2ViewSavedLayerSummaryschema
} from '@app/types/sdk';
import {
  hydrateTopologyViewOverlayState,
  setTopologyOverlayLayerStatuses,
  type TopologyOverlayRuntimeState
} from '@app/utils/topologyOverlay';

export const isCustomTopologyView = (view?: TopologyV2ViewCatalogItemschema | null): boolean => view?.origin === 'custom';

export interface TopologyViewBoundScope {
  scopeType: string;
  scopeId: string;
}

export const getTopologyViewBoundScope = (
  view?: TopologyV2ViewCatalogItemschema | null
): TopologyViewBoundScope | null => {
  if (!isCustomTopologyView(view) || view?.scopeMode !== 'current_scope') {
    return null;
  }
  const scopeType = typeof view.boundScope?.scopeType === 'string' ? view.boundScope.scopeType.trim() : '';
  const scopeId = typeof view.boundScope?.scopeId === 'string' ? view.boundScope.scopeId.trim() : '';
  if (!scopeType || !scopeId) {
    return null;
  }
  return {
    scopeType,
    scopeId
  };
};

export const partitionTopologyViews = (views: TopologyV2ViewCatalogItemschema[]): {
  system: TopologyV2ViewCatalogItemschema[];
  custom: TopologyV2ViewCatalogItemschema[];
} => ({
  system: views.filter((view) => !isCustomTopologyView(view)),
  custom: views.filter((view) => isCustomTopologyView(view))
});

export const upsertTopologyView = (
  views: TopologyV2ViewCatalogItemschema[],
  nextView: TopologyV2ViewCatalogItemschema
): TopologyV2ViewCatalogItemschema[] => {
  const index = views.findIndex((view) => view.viewId === nextView.viewId);
  if (index < 0) {
    return [...views, nextView];
  }
  const next = [...views];
  next[index] = nextView;
  return next;
};

export const removeTopologyView = (
  views: TopologyV2ViewCatalogItemschema[],
  viewId: string
): TopologyV2ViewCatalogItemschema[] => views.filter((view) => view.viewId !== viewId);

export const pickTopologyViewAfterDelete = (
  views: TopologyV2ViewCatalogItemschema[],
  deletedViewId: string,
  preferredBaseTemplateViewId?: string | null
): string => {
  const remaining = removeTopologyView(views, deletedViewId);
  if (preferredBaseTemplateViewId && remaining.some((view) => view.viewId === preferredBaseTemplateViewId)) {
    return preferredBaseTemplateViewId;
  }
  return (
    remaining.find((view) => view.viewId === 'org_overview')?.viewId ??
    remaining.find((view) => !isCustomTopologyView(view))?.viewId ??
    remaining[0]?.viewId ??
    ''
  );
};

export const buildTopologyViewHref = (
  viewId: string,
  scope?: { scopeType?: string | null; scopeId?: string | null }
): string => {
  const params = new URLSearchParams();
  const normalizedViewId = viewId.trim();
  if (normalizedViewId) {
    params.set('viewId', normalizedViewId);
  }
  const scopeType = (scope?.scopeType ?? '').trim();
  if (scopeType) {
    params.set('scopeType', scopeType);
  }
  const scopeId = (scope?.scopeId ?? '').trim();
  if (scopeId) {
    params.set('scopeId', scopeId);
  }
  const query = params.toString();
  return query ? `/app/topology-v2?${query}` : '/app/topology-v2';
};

export const hydrateResolvedTopologyViewOverlayState = (
  resolved: Pick<TopologyV2ResolveResponse, 'overlayState' | 'overlayStatuses'> | null | undefined
): TopologyOverlayRuntimeState =>
  setTopologyOverlayLayerStatuses(
    hydrateTopologyViewOverlayState(resolved?.overlayState),
    resolved?.overlayStatuses ?? []
  );

export const topologyViewSaveSummaryLines = (
  response: Pick<TopologyV2ViewSaveResponse, 'savedLayerSummary'>
): string[] => {
  const summary = response.savedLayerSummary;
  if (!summary) {
    return [];
  }
  return buildLayerSummaryLines(summary);
};

const buildLayerSummaryLines = (summary: TopologyV2ViewSavedLayerSummaryschema): string[] => {
  const lines: string[] = [];
  lines.push(`${summary.portableLayers} portable layer${summary.portableLayers === 1 ? '' : 's'} saved`);
  lines.push(
    `${summary.currentScopeLayers} current-scope layer${summary.currentScopeLayers === 1 ? '' : 's'} saved`
  );
  lines.push(`${summary.omittedLayers} layer${summary.omittedLayers === 1 ? '' : 's'} omitted`);
  return lines;
};
